import React, { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    autoUpdate,
    flip,
    FloatingPortal,
    offset,
    shift,
    useFloating,
    useTransitionStyles,
} from '@floating-ui/react';
import { createUseStyles, Text, useTheme } from '@Sage/hoi-poi-ui';

import styles from './styles.js';

const useStyles = createUseStyles(styles, 'SelectionLimitPopover');

const TABLE_ID = 's200uki-company-selection';
const CLOSE_DELAY_MS = 200;
const POPOVER_SELECTOR = '[data-selection-limit-popover]';

/** Virtual reference at the top-center of the checkbox; popover bottom-right attaches here. */
const createTopCenterReference = (element) => ({
    getBoundingClientRect() {
        const rect = element.getBoundingClientRect();
        const x = rect.left + rect.width / 2;
        const y = rect.top;

        return {
            width: 0,
            height: 0,
            x,
            y,
            top: y,
            left: x,
            right: x,
            bottom: y,
        };
    },
});

const SelectionLimitPopover = ({ checkboxColId, content, isEnabled }) => {
    const theme = useTheme();
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const activeTriggerRef = useRef(null);
    const closeTimerRef = useRef(null);

    const { refs, floatingStyles, context } = useFloating({
        open,
        placement: 'top-end',
        middleware: [offset(8), flip(), shift({ padding: 8 })],
        whileElementsMounted: autoUpdate,
    });

    const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
        duration: 200,
        initial: { opacity: 0 },
        open: { opacity: 1 },
        close: { opacity: 0 },
    });

    const getDisabledTrigger = useCallback(
        (target) => {
            const disabledCheckbox = target?.closest?.(
                `.ag-cell[col-id="${checkboxColId}"] .w5-cell-checkbox--disabled`,
            );
            if (disabledCheckbox) return disabledCheckbox;

            const cell = target?.closest?.(
                `.ag-cell[col-id="${checkboxColId}"].company-list-cell--checkbox-disabled`,
            );
            if (!cell) return null;

            return cell.querySelector('.w5-cell-checkbox') || cell;
        },
        [checkboxColId],
    );

    const isPopoverElement = useCallback(
        (element) => Boolean(element?.closest?.(POPOVER_SELECTOR)),
        [],
    );

    useLayoutEffect(() => {
        if (!open || !activeTriggerRef.current) return;

        refs.setReference(createTopCenterReference(activeTriggerRef.current));
    }, [open, refs]);

    useEffect(() => {
        if (!isEnabled || !content) {
            setOpen(false);
            activeTriggerRef.current = null;
            return undefined;
        }

        const tableEl = document.getElementById(TABLE_ID);
        if (!tableEl) return undefined;

        const clearCloseTimer = () => {
            if (closeTimerRef.current) {
                clearTimeout(closeTimerRef.current);
                closeTimerRef.current = null;
            }
        };

        const scheduleClose = () => {
            clearCloseTimer();
            closeTimerRef.current = setTimeout(() => {
                setOpen(false);
                activeTriggerRef.current = null;
            }, CLOSE_DELAY_MS);
        };

        const handleMouseOver = (event) => {
            const trigger = getDisabledTrigger(event.target);
            if (!trigger) return;

            clearCloseTimer();
            activeTriggerRef.current = trigger;
            setOpen(true);
        };

        const handleMouseOut = (event) => {
            if (!activeTriggerRef.current) return;

            const { relatedTarget } = event;
            if (isPopoverElement(relatedTarget)) return;
            if (relatedTarget && getDisabledTrigger(relatedTarget)) return;

            scheduleClose();
        };

        tableEl.addEventListener('mouseover', handleMouseOver);
        tableEl.addEventListener('mouseout', handleMouseOut);

        return () => {
            tableEl.removeEventListener('mouseover', handleMouseOver);
            tableEl.removeEventListener('mouseout', handleMouseOut);
            clearCloseTimer();
        };
    }, [content, getDisabledTrigger, isEnabled, isPopoverElement]);

    if (!isEnabled || !content) return null;

    return (
        <FloatingPortal>
            {isMounted && open && (
                <div
                    ref={refs.setFloating}
                    data-selection-limit-popover
                    className={classes.root}
                    style={{ ...floatingStyles, ...transitionStyles }}
                >
                    <div className={classes.content}>
                        <Text type="body" color={theme.colors.utility.textPrimary}>
                            {content}
                        </Text>
                    </div>
                </div>
            )}
        </FloatingPortal>
    );
};

SelectionLimitPopover.propTypes = {
    checkboxColId: PropTypes.string.isRequired,
    content: PropTypes.string,
    isEnabled: PropTypes.bool,
};

SelectionLimitPopover.displayName = 'SelectionLimitPopover';

export default memo(SelectionLimitPopover);
